import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import MyPlanSection from "@/components/subscriptions/MyPlanSection";
import {
  Plus, Search, CreditCard, TrendingUp, AlertTriangle, Users,
  IndianRupee, Loader2, Eye, Pause, Play, RotateCcw, Filter,
  Building2, Calendar, Clock, ChevronDown, X, FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  trial: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  expired: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  suspended: "bg-slate-200 text-slate-600 dark:bg-slate-600/30 dark:text-slate-400",
};

const PAYMENT_STYLES = {
  paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  failed: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
};

export default function BusinessSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    business_id: "",
    plan_id: "",
    billing_cycle: "monthly",
    custom_price: "",
    start_date: new Date().toISOString().split("T")[0],
    payment_status: "paid",
    auto_renew: true,
    grace_period_days: 7,
    notes: "",
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [subsRes, plansRes, statsRes, bizRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/subscriptions?${new URLSearchParams({
          ...(search && { search }),
          ...(planFilter && { plan_filter: planFilter }),
          ...(statusFilter && { status_filter: statusFilter }),
        })}`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/subscription-plans`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/subscriptions/stats`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/auth/my-businesses`, { credentials: "include" }),
      ]);
      if (subsRes.ok) setSubscriptions(await subsRes.json());
      if (plansRes.ok) setPlans(await plansRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (bizRes.ok) {
        const d = await bizRes.json();
        setBusinesses(Array.isArray(d) ? d : (d.businesses || []));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [search, planFilter, statusFilter]);

  // MyPlanSection fires this after a successful upgrade/downgrade so the admin
  // table below reflects the new subscription immediately.
  useEffect(() => {
    const h = () => fetchAll();
    window.addEventListener("subscriptions:changed", h);
    return () => window.removeEventListener("subscriptions:changed", h);
  }, []);

  const openCreate = () => {
    setForm({
      business_id: "",
      plan_id: "",
      billing_cycle: "monthly",
      custom_price: "",
      start_date: new Date().toISOString().split("T")[0],
      payment_status: "paid",
      auto_renew: true,
      grace_period_days: 7,
      notes: "",
    });
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!form.business_id) { toast.error("Select a business"); return; }
    if (!form.plan_id) { toast.error("Select a plan"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        custom_price: form.custom_price ? parseFloat(form.custom_price) : null,
        grace_period_days: parseInt(form.grace_period_days) || 7,
      };
      const res = await fetch(`${BACKEND_URL}/api/subscriptions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Subscription activated");
        setDialogOpen(false);
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create subscription");
      }
    } catch (e) {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (sub) => {
    setEditingSub(sub);
    setForm({
      business_id: sub.business_id,
      plan_id: sub.plan_id,
      billing_cycle: sub.billing_cycle,
      custom_price: sub.custom_price || "",
      start_date: sub.start_date?.split("T")[0] || "",
      payment_status: sub.payment_status || "paid",
      auto_renew: sub.auto_renew ?? true,
      grace_period_days: sub.grace_period_days ?? 7,
      notes: sub.notes || "",
    });
    setEditDialog(true);
  };

  const handleUpdate = async () => {
    if (!editingSub) return;
    setSaving(true);
    try {
      const payload = {
        plan_id: form.plan_id,
        billing_cycle: form.billing_cycle,
        custom_price: form.custom_price ? parseFloat(form.custom_price) : null,
        payment_status: form.payment_status,
        auto_renew: form.auto_renew,
        grace_period_days: parseInt(form.grace_period_days) || 7,
        notes: form.notes,
      };
      const res = await fetch(`${BACKEND_URL}/api/subscriptions/${editingSub.subscription_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Subscription updated");
        setEditDialog(false);
        setEditingSub(null);
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update");
      }
    } catch (e) {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (sub, newStatus, extend = false) => {
    const label = newStatus.charAt(0).toUpperCase() + newStatus.slice(1);
    if (!window.confirm(`${label} subscription for "${sub.business_name}"?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscriptions/${sub.subscription_id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus, extend }),
      });
      if (res.ok) { toast.success(`Subscription ${newStatus}`); fetchAll(); }
      else { const err = await res.json(); toast.error(err.detail || "Failed"); }
    } catch (e) { toast.error("Network error"); }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const isExpiringSoon = (d) => {
    if (!d) return false;
    const exp = new Date(d);
    const now = new Date();
    const diff = (exp - now) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff <= 7;
  };

  const activePlans = plans.filter(p => p.is_active);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
    </div>
  );

  return (
    <div className="space-y-8" data-testid="business-subscriptions-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Subscriptions</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">See your plan and upgrade or downgrade any time.</p>
        </div>
        <Button onClick={openCreate} data-testid="assign-subscription-btn" className="bg-amber-500 hover:bg-amber-400 text-white">
          <Plus className="w-4 h-4 mr-2" /> Assign Subscription
        </Button>
      </div>

      {/* Customer-facing plan panel */}
      <MyPlanSection />

      {/* Summary Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="subscription-stats">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center mb-3">
              <CreditCard className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total_active}</div>
            <div className="text-xs text-slate-400">Active Subscriptions</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center mb-3">
              <IndianRupee className="w-5 h-5 text-amber-500" />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">₹{stats.monthly_revenue?.toLocaleString("en-IN")}</div>
            <div className="text-xs text-slate-400">Monthly Revenue</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">{stats.expiring_this_month}</div>
            <div className="text-xs text-slate-400">Expiring This Month</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center mb-3">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total_trial}</div>
            <div className="text-xs text-slate-400">Trial Users</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="search-subscriptions" placeholder="Search business or owner..." className="pl-9"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={planFilter} onValueChange={v => setPlanFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px]" data-testid="plan-filter">
            <SelectValue placeholder="Filter by Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            {plans.map(p => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="subscriptions-table">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Business</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Plan</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Cycle</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Price</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Status</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Payment</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Expiry</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-400">
                    <CreditCard className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No subscriptions found
                  </td>
                </tr>
              ) : subscriptions.map(sub => (
                <tr key={sub.subscription_id} className="border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50/50 dark:hover:bg-slate-700/20 transition-colors"
                  data-testid={`sub-row-${sub.subscription_id}`}>
                  <td className="px-5 py-3.5">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">{sub.business_name}</div>
                    <div className="text-xs text-slate-400">{sub.owner_name} &middot; {sub.owner_email}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{sub.plan_name}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-xs text-slate-500 capitalize">{sub.billing_cycle}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">₹{sub.price?.toLocaleString("en-IN")}</span>
                    {sub.custom_price && <span className="text-[10px] text-amber-500 ml-1">(custom)</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_STYLES[sub.status] || ""}`}>
                      {sub.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full capitalize ${PAYMENT_STYLES[sub.payment_status] || ""}`}>
                      {sub.payment_status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className={`text-sm ${isExpiringSoon(sub.expiry_date) ? "text-red-500 font-semibold" : "text-slate-600 dark:text-slate-300"}`}>
                      {formatDate(sub.expiry_date)}
                    </div>
                    {isExpiringSoon(sub.expiry_date) && (
                      <span className="text-[10px] text-red-400">Expiring soon</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(sub)} title="Edit"
                        className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-amber-500 transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      {sub.status === "active" && (
                        <button onClick={() => changeStatus(sub, "suspended")} title="Suspend"
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500 transition-colors">
                          <Pause className="w-4 h-4" />
                        </button>
                      )}
                      {sub.status === "suspended" && (
                        <button onClick={() => changeStatus(sub, "active", true)} title="Reactivate"
                          className="p-1.5 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-slate-400 hover:text-emerald-500 transition-colors">
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      {sub.status === "expired" && (
                        <button onClick={() => changeStatus(sub, "active", true)} title="Renew"
                          className="p-1.5 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-slate-400 hover:text-emerald-500 transition-colors">
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Subscription Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold">Business</Label>
              <Select value={form.business_id} onValueChange={v => setForm({ ...form, business_id: v })}>
                <SelectTrigger data-testid="select-business">
                  <SelectValue placeholder="Select business" />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map(b => <SelectItem key={b.business_id} value={b.business_id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Plan</Label>
              <Select value={form.plan_id} onValueChange={v => setForm({ ...form, plan_id: v })}>
                <SelectTrigger data-testid="select-plan">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  {activePlans.map(p => (
                    <SelectItem key={p.plan_id} value={p.plan_id}>
                      {p.name} — ₹{p.monthly_price}/mo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Billing Cycle</Label>
                <Select value={form.billing_cycle} onValueChange={v => setForm({ ...form, billing_cycle: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Custom Price (₹)</Label>
                <Input type="number" placeholder="Leave blank for plan price" value={form.custom_price}
                  onChange={e => setForm({ ...form, custom_price: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Start Date</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs font-semibold">Payment Status</Label>
                <Select value={form.payment_status} onValueChange={v => setForm({ ...form, payment_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.auto_renew} onCheckedChange={v => setForm({ ...form, auto_renew: v })} />
                Auto Renew
              </label>
              <div>
                <Label className="text-xs font-semibold">Grace Period (days)</Label>
                <Input type="number" value={form.grace_period_days} onChange={e => setForm({ ...form, grace_period_days: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Notes</Label>
              <textarea className="w-full border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-sm min-h-[60px] bg-transparent"
                value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes..." />
            </div>
            <Button onClick={handleCreate} disabled={saving} data-testid="activate-subscription-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Activate Subscription
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Subscription Dialog */}
      <Dialog open={editDialog} onOpenChange={v => { setEditDialog(v); if (!v) setEditingSub(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Subscription — {editingSub?.business_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold">Plan</Label>
              <Select value={form.plan_id} onValueChange={v => setForm({ ...form, plan_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
                <SelectContent>
                  {plans.map(p => (
                    <SelectItem key={p.plan_id} value={p.plan_id}>
                      {p.name} — ₹{p.monthly_price}/mo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Billing Cycle</Label>
                <Select value={form.billing_cycle} onValueChange={v => setForm({ ...form, billing_cycle: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Custom Price (₹)</Label>
                <Input type="number" placeholder="Leave blank for plan price" value={form.custom_price}
                  onChange={e => setForm({ ...form, custom_price: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Payment Status</Label>
                <Select value={form.payment_status} onValueChange={v => setForm({ ...form, payment_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Grace Period (days)</Label>
                <Input type="number" value={form.grace_period_days} onChange={e => setForm({ ...form, grace_period_days: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.auto_renew} onCheckedChange={v => setForm({ ...form, auto_renew: v })} />
              Auto Renew
            </label>
            <div>
              <Label className="text-xs font-semibold">Notes</Label>
              <textarea className="w-full border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-sm min-h-[60px] bg-transparent"
                value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            <Button onClick={handleUpdate} disabled={saving} data-testid="update-subscription-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Subscription
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
